import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@/App.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Navbar } from '@/components/Navbar';
import { HeroSection } from '@/components/sections/HeroSection';
import { LegacySection } from '@/components/sections/LegacySection';
import { LogisticsSection } from '@/components/sections/LogisticsSection';
import { SelectionSection } from '@/components/sections/SelectionSection';
import { IntelSection } from '@/components/sections/IntelSection';
import { AiDebriefSection } from '@/components/sections/AiDebriefSection';
import { ReferencesSection } from '@/components/sections/ReferencesSection';
import { CaseStudiesSection } from '@/components/sections/CaseStudiesSection';
import { LiveSourcing } from '@/components/sections/LiveSourcing';
import { MarketingSection } from '@/components/sections/MarketingSection';
import { TeamSection } from '@/components/sections/TeamSection';
import { RewardModelSection } from '@/components/sections/RewardModelSection';
import { WibeFactorSection } from '@/components/sections/WibeFactorSection';
import { SpocSection } from '@/components/sections/SpocSection';
import { OrgStructureSection } from '@/components/sections/OrgStructureSection';
import { ComplianceSection } from '@/components/sections/ComplianceSection';
import { StrategicCommitment } from '@/components/sections/StrategicCommitment';
import { WhyWibeSection } from '@/components/sections/WhyWibeSection';
import { ValuesSection } from '@/components/sections/ValuesSection';
import { SustainabilitySection } from '@/components/sections/SustainabilitySection';
import { ClosingCTA } from '@/components/sections/ClosingCTA';
import { Footer } from '@/components/Footer';
import { ScrollProgress } from '@/components/ScrollProgress';
import { LanguageToggle } from '@/components/LanguageToggle';
import { PasswordGate } from '@/components/PasswordGate';
import { IntelApp } from '@/intel/IntelApp';
import AiDebrief from '@/intel/AiDebrief';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const SECTION_IDS = [
  'hero', 'team', 'references', 'case-studies', 'legacy', 'wibe-factor', 'spoc', 'org-structure', 'reward-model',
  'logistics', 'selection', 'compliance', 'intel', 'ai-debrief',
  'marketing', 'sustainability', 'values', 'why-wibe', 'strategic-commitment', 'closing',
];

function App() {
  const mainRef = useRef(null);
  const [activeSection, setActiveSection] = useState(0);
  const activeSectionRef = useRef(0);
  const isTransitioning = useRef(false);

  useEffect(() => { activeSectionRef.current = activeSection; }, [activeSection]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      SECTION_IDS.forEach((id, index) => {
        ScrollTrigger.create({
          trigger: `#${id}`,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => setActiveSection(index),
          onEnterBack: () => setActiveSection(index),
        });
      });
    }, mainRef);

    return () => ctx.revert();
  }, []);

  const scrollToSection = useCallback((index) => {
    const y = index === 0 ? 0 : `#${SECTION_IDS[index]}`;
    gsap.to(window, { scrollTo: { y, autoKill: false }, duration: 1.5, ease: 'power3.out' });
  }, []);

  const navigateWithTransition = useCallback((direction) => {
    const next = activeSectionRef.current + direction;
    if (next < 0 || next >= SECTION_IDS.length) return;

    gsap.killTweensOf(window);

    // Pre-calculate target Y synchronously to avoid lookup delay
    let targetY;
    if (next === 0) {
      targetY = 0;
    } else {
      const el = document.getElementById(SECTION_IDS[next]);
      targetY = el ? el.getBoundingClientRect().top + window.pageYOffset : 0;
    }

    gsap.to(window, {
      scrollTo: { y: targetY, autoKill: false },
      duration: 0.9,
      ease: 'power4.out',
      onComplete: () => { isTransitioning.current = false; },
    });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        navigateWithTransition(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        navigateWithTransition(-1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigateWithTransition]);

  return (
    <div ref={mainRef} className="min-h-screen bg-[#0a1628] text-white relative">
      <div className="noise-overlay" aria-hidden="true" />
      <Navbar onNavigate={scrollToSection} />
      <ScrollProgress
        sections={SECTION_IDS}
        activeIndex={activeSection}
        onDotClick={(i) => navigateWithTransition(i - activeSection)}
      />
      <LanguageToggle />
      <main>
        <HeroSection />
        <div className="section-divider" />
        <TeamSection />
        <div className="section-divider" />
        <ReferencesSection />
        <div className="section-divider" />
        <CaseStudiesSection />
        <div className="section-divider" />
        <LegacySection />
        <div className="section-divider" />
        <WibeFactorSection />
        <div className="section-divider" />
        <SpocSection />
        <div className="section-divider" />
        <OrgStructureSection />
        <div className="section-divider" />
        <RewardModelSection />
        <div className="section-divider" />
        <LogisticsSection />
        <div className="section-divider" />
        <SelectionSection />
        <div className="section-divider" />
        <ComplianceSection />
        <div className="section-divider" />
        <IntelSection />
        <div className="section-divider" />
        <AiDebriefSection />
        <div className="section-divider" />
        <MarketingSection />
        <div className="section-divider" />
        <SustainabilitySection />
        <div className="section-divider" />
        <ValuesSection />
        <div className="section-divider" />
        <WhyWibeSection />
        <div className="section-divider" />
        <StrategicCommitment />
        <div className="section-divider" />
        <ClosingCTA />
      </main>
      <Footer />
    </div>
  );
}

export default function Root() {
  const basename = window.location.pathname.startsWith('/preza') ? '/preza' : '/';
  const isPreza  = basename === '/preza';
  return (
    <BrowserRouter basename={basename}>
      <LanguageProvider>
        {isPreza ? (
          <Routes>
            <Route path="/app" element={<IntelApp />} />
            <Route path="/intel" element={<IntelApp />} />
            <Route path="/debrief" element={<AiDebrief />} />
            <Route path="/*" element={<App />} />
          </Routes>
        ) : (
        <PasswordGate>
          <Routes>
            <Route path="/app" element={<IntelApp />} />
            <Route path="/intel" element={<IntelApp />} />
            <Route path="/debrief" element={<AiDebrief />} />
            <Route path="/*" element={<App />} />
          </Routes>
        </PasswordGate>
        )}
      </LanguageProvider>
    </BrowserRouter>
  );
}
